<template>
  <div class="vessel-map-component-container">
    <div class="map-view-container">
      <div v-show="showMap" class="w-full h-full">
        <div id="map-view" class="w-full h-full"></div>
      </div>
      <div v-show="!showMap">
        <div class="no-map-view flex items-center justify-center">
          航路情報が見つかりません。
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Mapbox from "mapbox-gl";
import 'mapbox-gl/dist/mapbox-gl.css'
import LocalDbService from "@/services/localDbService";
import PanelCard from "../components/PanelCard.vue"
import moment from 'moment';

import { createNamespacedHelpers } from "vuex";
const { mapActions, mapGetters } = createNamespacedHelpers("fuelConsumption");

// Mixins
import { LocalizeMixin, DeviceInfoMixin, LayerMixin } from "@/mixins";
import { MAP_COLORS } from "../../../Constants.js";

const START_POINT_ID = "waypoint-start-port";
const GOAL_POINT_ID = "waypoint-goal-port"

const VOYAGE_LINE_ID = "voyage-line";

export default {
  mixins: [LocalizeMixin, DeviceInfoMixin, LayerMixin],

  components: {
    PanelCard,
  },
  props: {
    showMap: {
      type: Boolean,
      required: true,
    }
  },
  watch: {

  },
  data() {
    return {
      map: null,
      accessToken: process.env.VUE_APP_MAPBOX_TOKEN,
      defaultZoom: 3,
      mapStyleLocal: null,
      center: [133, 35],
      popup: null,
      hoveredLineId: null,
      // pointCircleRadius: 8,
      // expandedPointCircleRadius: 12,
    };
  },
  computed: {
    ...mapGetters([
      "getUsualRoute",
      "getRecomendedRoute",
      "getActualRoute",
      "getUsualRouteWaypoints",
      "getRecomendedRouteWaypoints",
      "getActualRoutesWaypoints",
      "getMapCenterPosition",
      "getActualAccumlatedDistance",
    ]),

    zoom() {
      return 4;
    },

    centerPoint() {
      if (!this.startPoint || !this.goalPoint) return this.center;
      if (this.startPoint.length !== 2 || this.goalPoint.length !== 2) return this.center;

      const lat = (this.startPoint[0] + this.goalPoint[0]) / 2;
      const lng = (this.startPoint[1] + this.goalPoint[1]) / 2;
      return [lat, lng];
    },
    usualRoute() {
      return this.getUsualRoute;
    },
    recomendedRoute() {
      return this.getRecomendedRoute;
    },
    actualRoute() {
      return this.getActualRoute;
    },
    actualRouteWaypoints() {
      return this.getActualRoutesWaypoints.map((item, index) => {
        return {
          id: `${ VOYAGE_LINE_ID }_${ index }`,
          date: moment.unix(item[0][2]).format("YYYY年MM月DD日"),
          distance: Math.floor(item.map(item => item[3]).reduce((sum, el) => {
            if (isNaN(el)) return sum;
            return sum + el;
          })),
          data: item
        };
      });
    },
  },
  methods: {

    parseComma(value) {
      if (typeof value !== "number") return "-";
      if (value) {
        return parseFloat(value.toFixed(0)).toLocaleString();
      } else {
        return "-";
      }
    },

    getFormatDegMinSec(val) {
      const value = Number(val)
      if (!value || typeof value !== "number") return "";
      const deg = Math.trunc(val);
      const min = Math.trunc((val - deg) * 60);
      const secValue = (val - deg) * 60 * 60 - (min * 60);
      const secInt = Math.trunc(secValue)
      const secDecimal = Math.trunc((secValue - secInt) * 10000);
      return `${ deg }°${ min }'${ secInt }"${ secDecimal }`;
    },

    setHoverVoyageActions() {
      this.actualRouteWaypoints.forEach((item) => {
        const id = item.id;
        const date = item.date;
        const startData = item.data[0];
        const endData = item.data.slice(-1)[0];
        const startTime = moment.unix(startData[2]);
        const endTime = moment.unix(endData[2]);
        const time = endTime.diff(startTime, "hours");
        const distance = this.parseComma(item.distance);
        this.setPopUp(id, date, time, distance);

      })
    },

    setPopUp(id, date, time, distance) {

      //出発港のポップアップを出す
      this.map.on('mousemove', id, (e) => {
        const startPopupHtml = `
        <p style="color:#B9B9B9;">${ date }</p>
        <p>航海距離　：${ distance }nm</p>
        <p>航海時間　：${ time }時間</p>
      `;

        this.popup.setLngLat(e.lngLat)
          .setHTML(startPopupHtml)
          .addTo(this.map);
      });

      //ホバーした線の色を変える、それ以外の線のホバーを解除する
      this.map.on('mousemove', id, (e) => {
        const hoveredLineId = e.features[0].id;

        this.hoveredLineId = e.features[0].id;
        this.map.setFeatureState(
          { source: `${ id }_source`, id: e.features[0].id },
          { hover: true }
        );
      });

      //すべての線のホバーを解除する
      this.map.on('mouseleave', id, (e) => {
        for (let i = 0;i < this.actualRouteWaypoints.length;i++) {
          this.map.setFeatureState(
            { source: `${ id }_source`, id: i },
            { hover: false }
          );
        }
      });

      //出発港のポップアップを消す
      this.map.on('mouseleave', id, (e) => {
        this.popup.remove();
      });
    },


    drawPopup() {
      this.popup = new Mapbox.Popup({ closeOnClick: false, closeButton: false })
    },

    drawLine(id, geometry, index) {
      const sourceId = `${ id }_source`;
      this.map.addSource(sourceId, {
        type: 'geojson',
        data: {
          type: "Feature",
          properties: {},
          geometry: geometry,
          id: index
        }
      });

      this.map.addLayer({
        id: id,
        type: "line",
        source: sourceId,
        layout: { "line-cap": "round" },
        paint: {
          "line-color": [
            'case',
            ['boolean', ['feature-state', 'hover'], false],
            MAP_COLORS.ACTUAL,
            MAP_COLORS.PERIOD_ACTUAL
          ],
          "line-width": 4,
        },
      });
    },


    createGeometry(points) {
      const coordinates = points.map(item => {
        return [item[0], item[1]]
      });
      return {
        type: "LineString",
        coordinates: coordinates,
      };
    },



    mapStyleLoad() {
      try {
        this.setLanguage();
        this.initActualRoute();
        this.drawPopup();
        this.setHoverVoyageActions();

      } catch (error) {
        console.error(error);
        throw error;
      }
    },

    setLanguage() {
      if (!this.isDefaultMapStyle(this.mapStyleLocal)) return;
      let lang = this.getBrowserLanguage()
      let local = "name_" + lang;
      if (!this.map) return;
      let textLayerIds = this.getTextLayerIds();
      textLayerIds.forEach(id => {
        this.map.setLayoutProperty(id, "text-field", ["get", local]);
      });
    },

    addControl() {
      const mapStyleChangerControl = {
        onAdd: _ => {
          this._container = document.createElement("div");
          this._container.classList.add("mapboxgl-ctrl", "mapboxgl-ctrl-group");
          this._container.classList.add("custom-control");
          return this._container;
        },
        getDefaultPosition: () => {
          return "top-right";
        },
        onRemove: () => {
          this._container.parentNode.removeChild(this._container);
          this._map = undefined;
        }
      };
      this.map.addControl(mapStyleChangerControl);

      let nav = new Mapbox.NavigationControl();
      this.map.addControl(nav, 'bottom-left');
    },
    initActualRoute() {
      this.actualRouteWaypoints.forEach((item, index) => {
        this.drawLine(item.id, this.createGeometry(item.data), index);
      })

    },
    async initMap() {
      try {
        this.mapStyleLocal = await LocalDbService.getMapStyle();
        Mapbox.prewarm();
        Mapbox.maxParallelImageRequests = 10;
        Mapbox.accessToken = this.accessToken;
        this.map = new Mapbox.Map({
          container: "map-view",
          style: this.mapStyleLocal,
          center: this.centerPoint,
          zoom: this.zoom
        });
        this.map.on("style.load", this.mapStyleLoad);

      } catch (error) {
        this.$vs.notify({
          title: "マップ情報の取得に失敗しました。",
          color: "danger",
          iconPack: "feather",
          icon: "icon-x-square",
        });
        throw error;
      }
    },
  },

  mounted() {
  },
}
</script>


<style lang="scss">
@import "@/assets/scss/aisea/_variables.scss";
@import "@/assets/scss/aisea/_typography.scss";
@import "src/assets/scss/aisea/fuel-consumption";

$view-height: 320px;

.vessel-map-component-container {
  .map-view-container {
    background: $aisea-dark-blue !important;
    height: $view-height;
    border-radius: 8px;
    #map-view {
      border-radius: 8px;
    }

    .mapboxgl-popup-content {
      background-color: $map-popup-background-color;
      color: $aisea-pure-white;
    }

    .mapboxgl-popup-tip {
      visibility: hidden;
    }
  }

  .mapboxgl-popup-close-button {
    display: none;
  }

  .no-map-view {
    width: 100%;
    height: $view-height;
    background-color: $aisea-dark-blue;
    color: $aisea-pure-white;
    font-size: 24px;
    border-radius: 8px;

    p {
      text-align: center;
    }
  }
}
</style>